export const sliderScaleOptions = [
  { value: "4", label: "4 Point Scale" },
  { value: "5", label: "5 Point Scale" },
  { value: "7", label: "7 Point Scale" },
];

const splitSliderOptionRaw = (raw) => {
  if (Array.isArray(raw)) {
    return raw.map((item) => String(item ?? ""));
  }

  if (typeof raw === "string") {
    if (raw.includes("\n") || raw.includes("\r")) {
      return raw.split(/\r?\n/).map((line) => line);
    }

    if (raw.includes(",")) {
      return raw.split(",").map((line) => line);
    }

    if (raw.trim()) {
      return [raw];
    }
  }

  return [];
};

export const countSliderOptionLabels = (raw) => {
  const lines = splitSliderOptionRaw(raw).map((line) =>
    String(line ?? "").trim(),
  );

  while (lines.length > 0 && !lines[lines.length - 1]) {
    lines.pop();
  }

  return lines.length;
};

export const resolveSliderScaleType = (scaleType, options) => {
  const fromScale = parseInt(scaleType, 10);

  if (Number.isInteger(fromScale) && fromScale >= 1) {
    return String(fromScale);
  }

  const fromOptions = countSliderOptionLabels(options);

  if (fromOptions >= 1) {
    return String(fromOptions);
  }

  return null;
};

export const getSliderScaleSelectOptions = (currentScaleType) => {
  const options = [...sliderScaleOptions];
  const current = resolveSliderScaleType(currentScaleType, null);

  if (current && !options.some((option) => String(option.value) === current)) {
    options.push({
      value: current,
      label: `${current} Point Scale`,
    });
  }

  return options;
};

export const parseSliderOptionLabels = (raw, scalePoints) => {
  const total = parseInt(scalePoints, 10);

  if (!Number.isInteger(total) || total < 1) {
    return [];
  }

  const normalized = splitSliderOptionRaw(raw).map((line) =>
    String(line ?? "").trim(),
  );

  while (normalized.length < total) {
    normalized.push("");
  }

  return normalized.slice(0, total);
};

export const isMissingSliderOptionLabel = (label) => {
  const text = String(label ?? "").trim();
  return !text;
};

export const isValidSliderScaleType = (scalePoints) => {
  const total = parseInt(scalePoints, 10);

  if (!Number.isInteger(total) || total < 1) {
    return false;
  }

  if (sliderScaleOptions.some((option) => Number(option.value) === total)) {
    return true;
  }

  return total >= 2 && total <= 10;
};

export const getSliderOptionsValidationError = ({
  options,
  scalePoints,
  showOnlyEmojis = false,
  inputTitle = "Slider",
} = {}) => {
  const title = String(inputTitle || "Slider").trim() || "Slider";

  if (!isValidSliderScaleType(scalePoints)) {
    return `${title}: select a Scale Type.`;
  }

  if (showOnlyEmojis) {
    return null;
  }

  const total = parseInt(scalePoints, 10);
  const labels = parseSliderOptionLabels(options, total);
  const missingIndexes = [];

  labels.forEach((label, index) => {
    if (isMissingSliderOptionLabel(label)) {
      missingIndexes.push(index + 1);
    }
  });

  if (missingIndexes.length === 0) {
    return null;
  }

  const pointsList = missingIndexes.join(", ");

  return `${title}: enter a label for every scale point (${total} required). Missing labels at point(s): ${pointsList}.`;
};

export const joinSliderOptionLabels = (labels = []) =>
  (Array.isArray(labels) ? labels : [])
    .map((label) => String(label ?? "").trim())
    .join("\n");

export const toSliderToggleValue = (value) => {
  if (value === true || value === 1 || value === "1") return true;
  if (typeof value === "string" && value.trim().toLowerCase() === "true") {
    return true;
  }
  return false;
};

export const resolveSliderToggleFlags = (input = {}) => {
  const config =
    input?.slider_config && typeof input.slider_config === "object"
      ? input.slider_config
      : {};

  return {
    show_emojis:
      toSliderToggleValue(input.show_emojis) ||
      toSliderToggleValue(config.show_emojis),
    show_stars:
      toSliderToggleValue(input.show_stars) ||
      toSliderToggleValue(config.show_stars),
    show_only_emojis:
      toSliderToggleValue(input.show_only_emojis) ||
      toSliderToggleValue(config.show_only_emojis),
  };
};

export const inputTypeOptions = [
  { value: "name", label: "Name" },
  { value: "email", label: "Email" },
  { value: "signature", label: "Signature" },
  { value: "slider", label: "Slider Input" },
  { value: "record-video", label: "Record Video" },
  { value: "paragraph", label: "Paragraph" },
  { value: "dropdown", label: "Dropdown" },
  { value: "text", label: "Text Field" },
  { value: "number", label: "Number Field" },
  { value: "date", label: "Date Field" },
  { value: "checkbox", label: "Check Box" },
  { value: "radio", label: "Radio Buttons" },
  { value: "radio-list", label: "Radio List" },
  { value: "checkbox-list", label: "Checkbox List" },
  { value: "file", label: "Upload a File" },
  { value: "multiple-files", label: "Upload Multiple Files" },
];

export const dateTypes = [
  { value: "date-range", label: "Date Range" },
  { value: "single-date", label: "Single Date" },
  { value: "date-time", label: "Date & Time" },
];

export const fileTypes = [
  { value: "image/*", label: "JPG, PNG, JPEG" },
  { value: "application/pdf", label: "PDF" },
  { value: "video/*", label: "Video Files" },
];

export const isFileUploadInputType = (type) =>
  type === "file" || type === "multiple-files";

export const buttonOptions = [
  { value: "start", label: "Start" },
  { value: "next", label: "Next" },
  { value: "finish", label: "Finish" },
];

const DECIMAL_COMMA_PLACEHOLDER = "\u0000";

const choiceOptionStringNeedsTokenSplit = (text) =>
  /[,|\n\r]/.test(String(text ?? ""));

export const splitChoiceOptionTokens = (text) => {
  const trimmed = String(text ?? "").trim();

  if (!trimmed) {
    return [];
  }

  if (trimmed.includes("\n") || trimmed.includes("\r")) {
    return trimmed.split(/\r?\n/).map((line) => String(line ?? ""));
  }

  if (trimmed.includes("|")) {
    return trimmed.split("|").map((part) => String(part ?? ""));
  }

  const protectedDecimals = trimmed.replace(
    /(\d),(\d)/g,
    `$1${DECIMAL_COMMA_PLACEHOLDER}$2`,
  );

  return protectedDecimals
    .split(",")
    .map((part) =>
      String(part).split(DECIMAL_COMMA_PLACEHOLDER).join(",").trim(),
    );
};

const parseChoiceOptionTokensFromRaw = (raw, { keepEmpty = false } = {}) => {
  if (Array.isArray(raw)) {
    if (
      raw.length === 1 &&
      typeof raw[0] === "string" &&
      choiceOptionStringNeedsTokenSplit(raw[0])
    ) {
      const tokens = splitChoiceOptionTokens(raw[0]);

      return keepEmpty
        ? tokens.length > 0
          ? tokens
          : [""]
        : tokens.filter((token) => String(token).trim() !== "");
    }

    const lines = raw.map((opt) => String(opt ?? ""));

    return keepEmpty
      ? lines.length > 0
        ? lines
        : [""]
      : lines.map((line) => line.trim()).filter((line) => line !== "");
  }

  if (typeof raw === "string") {
    const tokens = splitChoiceOptionTokens(raw);

    return keepEmpty
      ? tokens.length > 0
        ? tokens
        : [""]
      : tokens.filter((token) => String(token).trim() !== "");
  }

  return keepEmpty ? [""] : [];
};

export const parseChoiceOptionLines = (raw) =>
  parseChoiceOptionTokensFromRaw(raw, { keepEmpty: false });


export const parseChoiceOptionEditorLines = (raw) => {
  if (Array.isArray(raw)) {
    if (
      raw.length === 1 &&
      typeof raw[0] === "string" &&
      choiceOptionStringNeedsTokenSplit(raw[0])
    ) {
      return parseChoiceOptionEditorLines(raw[0]);
    }

    const lines = raw.map((opt) => String(opt ?? ""));
    return lines.length > 0 ? lines : [""];
  }

  if (typeof raw !== "string") {
    return [""];
  }

  if (raw.includes("\n") || raw.includes("\r")) {
    return raw.split(/\r?\n/).map((line) => String(line ?? ""));
  }

  if (!raw.trim()) {
    return [""];
  }

  if (raw.includes("|") || raw.includes(",")) {
    const tokens = splitChoiceOptionTokens(raw);
    return tokens.length > 0 ? tokens : [""];
  }

  return [raw];
};

export const joinChoiceOptionLines = (lines) =>
  (Array.isArray(lines) ? lines : [])
    .map((line) => String(line ?? "").trim())
    .filter(Boolean)
    .join("\n");

export const joinChoiceOptionEditorLines = (lines) => {
  const normalized = (Array.isArray(lines) ? lines : []).map((line) =>
    String(line ?? ""),
  );

  if (normalized.length === 0) {
    return "";
  }

  return normalized.join("\n");
};

export const hasChoiceOptionLogos = (logos) =>
  Array.isArray(logos) &&
  logos.some((url) => typeof url === "string" && url.trim() !== "");

export const resolveShowLogosFlag = (input = {}) =>
  Boolean(input.show_logos) || hasChoiceOptionLogos(input.option_logos);

export const isChoiceWithLogosSupport = (type) => {
  const normalized = String(type || "")
    .trim()
    .toLowerCase();

  return (
    normalized === "radio" ||
    normalized === "checkbox" ||
    normalized === "dropdown"
  );
};

export const alignOptionLogos = (logos, count) => {
  const source = Array.isArray(logos) ? logos : [];
  const total = Math.max(0, Number(count) || 0);

  return Array.from({ length: total }, (_, index) =>
    typeof source[index] === "string" ? source[index] : "",
  );
};

export const getChoiceLogosValidationError = ({
  inputType,
  options,
  showLogos = false,
  optionLogos = [],
  inputTitle = "Choice",
} = {}) => {
  if (!isChoiceWithLogosSupport(inputType)) {
    return null;
  }

  if (!showLogos) {
    return null;
  }

  const title = String(inputTitle || "Choice").trim() || "Choice";
  const optionsArray = parseChoiceOptionLines(options);

  if (optionsArray.length === 0) {
    return `${title}: add at least one option when With logos is enabled.`;
  }

  const logos = alignOptionLogos(optionLogos, optionsArray.length);
  const missingIndexes = [];

  optionsArray.forEach((option, index) => {
    if (!logos[index] || !String(logos[index]).trim()) {
      missingIndexes.push(index + 1);
    }
  });

  if (missingIndexes.length === 0) {
    return null;
  }

  const pointsList = missingIndexes.join(", ");

  return `${title}: upload a logo for every option. Missing logo(s) at option(s): ${pointsList}.`;
};

export const buildChoiceInputResponseFields = (input, mainForm) => {
  const optionsArray = parseChoiceOptionLines(input.options);

  if (!isChoiceWithLogosSupport(input.type)) {
    return { input_options: optionsArray };
  }

  const showLogos = Boolean(
    mainForm.getFieldValue(`show_logos_${input.id}`) ??
      resolveShowLogosFlag(input),
  );

  if (!showLogos) {
    return {
      input_options: optionsArray,
      show_logos: false,
      option_logos: [],
    };
  }

  return {
    input_options: optionsArray,
    show_logos: true,
    option_logos: alignOptionLogos(input.option_logos, optionsArray.length),
  };
};
